import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleCheck, Receipt, Wallet } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";
import { useMoney } from "@/shared/ui";
import { isFromEarlierDay, paymentLine, waitingTotal, type KitchenChip, type PickupRow } from "@/modules/pos/lib/deliveryWaiting";
import { endpoints } from "@/lib/api/endpoints";
import type { RestaurantSettings } from "@/lib/api/types";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { ItemsToggle, toggleIn } from "./DeliveryLane";
import { WaitingOrderLines } from "./WaitingOrderLines";

/**
 * «الاستلام من المطعم» — the Delivery tab's pickups (B6 · P24b; a list of rows
 * in the 2026-10-26 redesign): the bills collected at the counter — direct
 * pickups, platform orders their courier collects, and closed-but-unpaid
 * pickups. A row opens its bill (click or Enter), and «N items ⌄» shows its lines
 * under it without opening anything. Each row: the number, where it came from (a pill), who (or the
 * platform's order number), its state (the kitchen's, «Waiting for ⟨platform⟩'s
 * courier» once ready, or «Collected · unpaid»), the total and how it is paid,
 * and one action that opens the bill for the next step — on its settle sheet
 * when the cheque is already closed (T8's hand-off).
 *
 * Also used, titled «Delivery orders», for the own delivery bags of a session
 * that may not read the dispatch board (no lanes for it).
 */
export function PickupOrdersTable({
  rows,
  title,
  hint,
  testId = "pickup-card",
  canOpen,
}: {
  rows: readonly PickupRow[];
  title: string;
  /** One line under the title (the pickups section's own). */
  hint?: string;
  testId?: string;
  canOpen: boolean;
}) {
  const t = useTranslate();
  const money = useMoney();
  // The store's clock, from the till's shared settings cache: a pickup still
  // open from an earlier business day is flagged (2026-10-27).
  const settings = useApiQuery<RestaurantSettings>(queryKeys.settings, endpoints.restaurant.settings, {
    retry: false,
  }).data;
  const clock = settings ? { dayStartHour: settings.dayStartHour, timezone: settings.timezone } : null;
  const now = new Date();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const open = (bill: PickupRow["bill"]) =>
    navigate(`/${locale}/pos/invoices/${bill.invoiceId}`, {
      state: bill.status === "CLOSED" ? ({ settle: true } satisfies InvoiceHandoff) : null,
    });

  return (
    <section data-ui="dl-section" data-testid={testId} aria-label={title} className="flex flex-col">
      <header data-ui="dl-lane-head" className="flex flex-wrap items-center">
        <span aria-hidden="true" data-ui="dl-dot" data-tone="purple" />
        <h2 data-ui="dl-lane-title">{title}</h2>
        <span data-ui="dl-count" data-testid={`${testId}-count`} className="tabular-nums">
          {rows.length}
        </span>
        {hint ? <span data-ui="dl-lane-hint">{hint}</span> : null}
      </header>

      <div data-ui="dl-table" role="table" aria-label={title}>
        {rows.length === 0 ? (
          <p data-ui="dl-empty" data-testid={`${testId}-empty`} className="flex items-center justify-center">
            <CircleCheck aria-hidden="true" size={16} strokeWidth={2} />
            <span>{t((m) => m.dispatch.pickupEmpty)}</span>
          </p>
        ) : (
          rows.map(({ bill, kitchen }) => {
            const seq = bill.dailySequence;
            const pay = paymentLine(bill);
            const name = bill.customerName?.trim();
            const platform = bill.aggregatorId ? bill.aggregatorName?.trim() || null : null;
            const closed = bill.status === "CLOSED";
            const due = waitingTotal(bill);
            const source = platform
              ? { label: platform, tone: "orange" }
              : bill.channel === "DELIVERY"
                ? { label: t((m) => m.dispatch.pickupSourceDelivery), tone: "blue" }
                : { label: t((m) => m.dispatch.pickupSourceDirect), tone: "purple" };
            const payText =
              pay === "paid"
                ? platform
                  ? t((m) => m.dispatch.pickupPayOnPlatform, { platform })
                  : t((m) => m.dispatch.payPaid)
                : closed
                  ? t((m) => m.dispatch.pickupPayNotRecorded)
                  : t((m) => m.dispatch.pickupPayOnCollect);
            const actionLabel = closed
              ? t((m) => m.dispatch.pickupActCollect, { amount: money(due) })
              : platform
                ? t((m) => m.dispatch.pickupActCourier, { platform })
                : bill.channel === "DELIVERY"
                  ? t((m) => m.dispatch.pickupOpenBill)
                  : t((m) => m.dispatch.pickupActHandCollect);
            return (
              <div key={bill.invoiceId} data-ui="dl-tgroup">
              <div
                role="row"
                data-ui="dl-trow"
                data-openable={canOpen ? "" : undefined}
                data-testid={`pickup-row-${seq}`}
                tabIndex={canOpen ? 0 : undefined}
                aria-label={canOpen ? t((m) => m.dispatch.pickupOpenBillAria, { sequence: seq }) : undefined}
                onClick={canOpen ? () => open(bill) : undefined}
                onKeyDown={
                  canOpen
                    ? (event) => {
                        if (event.key === "Enter") open(bill);
                      }
                    : undefined
                }
              >
                <span role="cell" data-ui="dl-no" className="tabular-nums">
                  <bdi dir="ltr">#{seq}</bdi>
                </span>
                <span role="cell">
                  <span data-ui="dl-pill" data-tone={source.tone}>
                    <bdi>{source.label}</bdi>
                  </span>
                </span>
                <span role="cell" className="flex min-w-0 flex-col">
                  <span data-ui="dl-name" className="truncate">
                    <bdi>{(platform ? bill.platformOrderRef?.trim() : null) || name || platform || t((m) => m.dispatch.waitingNoName)}</bdi>
                  </span>
                  <span data-ui="dl-sub" className="truncate" data-testid={`pickup-sub-${seq}`}>
                    {platform ? (
                      name ? <bdi>{name}</bdi> : t((m) => m.dispatch.pickupRefOn, { platform })
                    ) : bill.customerPhone ? (
                      <bdi dir="ltr">{bill.customerPhone}</bdi>
                    ) : null}
                  </span>
                  <span className="flex">
                    <ItemsToggle
                      seq={seq}
                      count={bill.plates?.chargeable ?? 0}
                      open={expanded.has(bill.invoiceId)}
                      onToggle={() => setExpanded((current) => toggleIn(current, bill.invoiceId))}
                      testId={`pickup-items-${seq}`}
                    />
                  </span>
                </span>
                <span role="cell" className="flex">
                  {closed ? (
                    <span data-ui="dl-pill" data-tone="amber" data-testid={`pickup-state-${seq}`}>
                      {t((m) => m.dispatch.pickupStateClosedUnpaid)}
                    </span>
                  ) : isFromEarlierDay(bill, now, clock) ? (
                    <span data-ui="dl-pill" data-tone="red" data-testid={`pickup-state-${seq}`} data-earlier-day="">
                      {t((m) => m.dispatch.pickupStateEarlierDay)}
                    </span>
                  ) : platform && kitchen.kind === "ready" ? (
                    <span data-ui="dl-pill" data-tone="grey" data-testid={`pickup-state-${seq}`}>
                      <bdi>{t((m) => m.dispatch.pickupStateCourier, { platform })}</bdi>
                    </span>
                  ) : (
                    <KitchenChipView kitchen={kitchen} testId={`pickup-kitchen-${seq}`} />
                  )}
                </span>
                <span role="cell" className="flex min-w-0 flex-col">
                  <span data-ui="dl-money" className="tabular-nums" data-testid={`pickup-total-${seq}`}>
                    {money(due)}
                  </span>
                  <span data-ui="dl-pay" className="truncate">
                    <bdi>{payText}</bdi>
                  </span>
                </span>
                <span role="cell" className="flex">
                  {canOpen ? (
                    <button
                      type="button"
                      data-ui="dl-btn"
                      data-wide=""
                      data-tone="primary"
                      data-testid={`pickup-open-${seq}`}
                      aria-label={t((m) => m.dispatch.pickupOpenBillAria, { sequence: seq })}
                      onClick={(event) => {
                        event.stopPropagation();
                        open(bill);
                      }}
                      className="flex items-center justify-center"
                    >
                      {closed ? (
                        <Wallet aria-hidden="true" size={14} strokeWidth={2} />
                      ) : (
                        <Receipt aria-hidden="true" size={14} strokeWidth={2} />
                      )}
                      <span className="truncate">{actionLabel}</span>
                    </button>
                  ) : null}
                </span>
              </div>
              {expanded.has(bill.invoiceId) ? (
                <div data-ui="dl-lines">
                  <WaitingOrderLines invoiceId={bill.invoiceId} testId={`pickup-lines-${seq}`} />
                </div>
              ) : null}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

/** The kitchen column's chip — the waiting table's words and colours. */
export function KitchenChipView({ kitchen, testId }: { kitchen: KitchenChip; testId: string }) {
  const t = useTranslate();
  return (
    <span
      data-ui="waiting-kitchen"
      data-state={kitchen.kind}
      data-testid={testId}
      className="flex items-center whitespace-nowrap"
    >
      <span aria-hidden="true" data-ui="waiting-kitchen-dot" />
      <span>
        {kitchen.kind === "ready"
          ? kitchen.minutes >= 60
            ? t((m) => m.dispatch.kitchenReadyForHours, { count: Math.floor(kitchen.minutes / 60) })
            : kitchen.minutes > 0
              ? t((m) => m.dispatch.kitchenReadyFor, { count: kitchen.minutes })
              : t((m) => m.dispatch.kitchenReadyNow)
          : kitchen.kind === "preparing"
            ? t((m) => m.dispatch.kitchenPreparing)
            : t((m) => m.dispatch.kitchenNotSent)}
      </span>
    </span>
  );
}
